import { useState } from "react";
import WeTravelModal from "../cocon/WeTravelModal";
import useScrollReveal from "../../hooks/useScrollReveal";

const formulas = [
  {
    id: "immersion",
    tag: "Immersion — Valouse",
    title: "5 jours · 4 nuits",
    desc: "Le cœur de l'expérience.",
    dateLine: "7 → 11 septembre 2026",
    price: "1 488 € TTC",
    payment: "Paiement possible en plusieurs mensualités sans frais",
    btnLabel: "Je choisis l'immersion",
    url: "https://www.wetravel.com/checkout_embed?uuid=4257488537&source=direct_link",
    accent: false,
    btnBg: "#C5A059",
  },
  {
    id: "preparation-traversee",
    tag: "Préparation & Traversée",
    title: "Avant · Pendant",
    desc: "Entrer progressivement dans le processus,\npuis vivre l'immersion à Valouse.",
    dateLine: "25 juillet → 11 septembre 2026",
    price: "1 688 € TTC",
    payment: "Paiement possible en plusieurs mensualités sans frais",
    btnLabel: "Je choisis cette traversée",
    url: "https://www.wetravel.com/checkout_embed?uuid=9319532325&source=direct_link",
    accent: false,
  },
  {
    id: "traversee-integration",
    tag: "Traversée & Intégration",
    title: "Pendant · Après",
    desc: "Vivre l'immersion à Valouse,\npuis laisser s'ancrer ce qui a été traversé.",
    dateLine: "7 septembre → 31 octobre 2026",
    price: "1 688 € TTC",
    payment: "Paiement possible en plusieurs mensualités sans frais",
    btnLabel: "Je choisis cette traversée",
    url: "https://www.wetravel.com/checkout_embed?uuid=1950533945&source=direct_link",
    accent: false,
  },
  {
    id: "cycle-integral",
    tag: "Expérience complète",
    title: "Cycle initiatique intégral",
    desc: "Avant · Pendant · Après\nLe mouvement complet.",
    dateLine: "25 juillet → 31 octobre 2026",
    price: "1 888 € TTC",
    payment: "Jusqu'à 9 mensualités sans frais",
    btnLabel: "Je m'engage dans le cycle initiatique",
    url: "https://www.wetravel.com/checkout_embed?uuid=4599176313&source=direct_link",
    accent: true,
  },
];

/* ═══ CONSERVATION TEMPORAIRE — anciens textes des formules ═══
 *
 * Immersion:
 *   desc: "Le cœur de l'expérience. L'immersion au Hameau, du 7 au 11 septembre 2026."
 *   (pas de dateLine séparée)
 *
 * Préparation & Traversée:
 *   title: "Avant + Pendant"
 *   desc: "Entrer progressivement dans le processus, puis vivre l'immersion à Valouse."
 *
 * Traversée & Intégration:
 *   title: "Pendant + Après"
 *   desc: "Vivre l'immersion à Valouse, puis laisser s'ancrer ce qui a été traversé."
 *
 * Cycle intégral:
 *   desc: "Avant · Pendant · Après — Le mouvement complet."
 *
 * ═══ FIN CONSERVATION TEMPORAIRE ═══ */

const hoverColors = {
  "immersion": { bg: "#F3E5CF", text: "#3F2A24" },
  "preparation-traversee": { bg: "#3F2A24", text: "#F3E5CF" },
  "traversee-integration": { bg: "#3F2A24", text: "#F3E5CF" },
  "cycle-integral": { bg: "#9E9590", text: "#ffffff" },
};

const FormulasSection = () => {
  const [modalUrl, setModalUrl] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const headerReveal = useScrollReveal({ threshold: 0.2 });
  const poeticReveal = useScrollReveal({ threshold: 0.15 });
  const cardsReveal = useScrollReveal({ threshold: 0.08 });

  const openModal = (url) => {
    setModalUrl(url);
    setModalOpen(true);
  };

  return (
    <section id="formules" className="py-20 sm:py-32 px-6 bg-[#c8b5a5]">
      <div className="max-w-3xl mx-auto">

        {/* En-tête */}
        <div
          ref={headerReveal.ref}
          className={`text-center mb-20 space-y-4 scroll-reveal-slow ${headerReveal.isVisible ? "visible" : ""}`}
        >
          <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#F9F0E0]">
            Les formules
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-[#3F2A24]">
            Choisir sa traversée
          </h2>
        </div>

        {/* Texte poétique d'ouverture */}
        <div
          ref={poeticReveal.ref}
          className={`max-w-lg mx-auto text-center mb-14 space-y-12 font-serif text-base sm:text-lg leading-[2.2] text-[#3F2A24]/80 scroll-reveal ${poeticReveal.isVisible ? "visible" : ""}`}
        >
          <div className="space-y-4">
            <p>
              Si quelque chose en toi reconnaît ce passage,
              <br />alors inutile de forcer.
            </p>
            <p className="text-[#3F2A24] italic">Tu sais déjà.</p>
            <p className="text-[#3F2A24]/80 mt-2">Il reste simplement à choisir comment.</p>
          </div>

          <div className="w-10 h-[1px] bg-white/40 mx-auto" />

          <div className="space-y-4">
            <p className="text-[#3F2A24]">COCON se vit comme un mouvement.</p>
            <p>
              Certains entrent avant.
              <br />D'autres au cœur.
              <br />D'autres prolongent après.
            </p>
          </div>

          <div className="w-10 h-[1px] bg-white/40 mx-auto" />

          <div className="space-y-2">
            <p>Trois manières d'y prendre part.</p>
            <p className="text-[#3F2A24] font-medium">Une même profondeur.</p>
          </div>
        </div>

        {/* ═══ CARTES ═══ */}
        <div ref={cardsReveal.ref} className="space-y-8">

          {/* ─── 1. IMMERSION — Point d'entrée ─── */}
          <div className={`scroll-reveal scroll-reveal-delay-1 ${cardsReveal.isVisible ? "visible" : ""}`}>
            <FormulaCard formula={formulas[0]} onBook={openModal} />
          </div>

          {/* ─── 2 & 3 côte à côte ─── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className={`scroll-reveal scroll-reveal-delay-2 ${cardsReveal.isVisible ? "visible" : ""}`}>
              <FormulaCard formula={formulas[1]} onBook={openModal} />
            </div>
            <div className={`scroll-reveal scroll-reveal-delay-3 ${cardsReveal.isVisible ? "visible" : ""}`}>
              <FormulaCard formula={formulas[2]} onBook={openModal} />
            </div>
          </div>

          {/* ─── 4. CYCLE INTÉGRAL — évidence finale ─── */}
          <div className={`pt-4 scroll-reveal scroll-reveal-delay-4 ${cardsReveal.isVisible ? "visible" : ""}`}>
            <FormulaCard formula={formulas[3]} onBook={openModal} />
          </div>

        </div>
      </div>

      <WeTravelModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        url={modalUrl}
        loadingTitle="Préparation de votre espace…"
        loadingSubtitle="Réservation sécurisée"
      />
    </section>
  );
};

const FormulaCard = ({ formula, onBook }) => {
  const { id, tag, title, desc, dateLine, price, payment, btnLabel, accent, btnBg } = formula;
  const hover = hoverColors[id];
  const baseBg = btnBg || (accent ? '#C5A059' : '#8B7D5E');

  return (
    <div
      className={`formula-card-premium rounded-2xl bg-[#F3E5CF] text-center space-y-4 flex flex-col ${
        accent ? "p-10 sm:p-14 border border-[#C5A059]/40 space-y-6" : "p-6 sm:p-8"
      }`}
      data-testid={`formula-${id}`}
    >
      <p className={`text-[10px] sm:text-xs tracking-[0.3em] uppercase font-medium ${
        accent ? "text-[#C5A059]" : "text-[#5C4A3D]"
      }`}>
        {tag}
      </p>
      <h3 className={`font-serif text-[#3F2A24] ${
        accent ? "text-2xl sm:text-3xl" : "text-xl sm:text-2xl"
      }`}>
        {title}
      </h3>
      {accent && (
        <p className="text-sm sm:text-base text-[#5C4A3D] tracking-wide whitespace-pre-line">{desc}</p>
      )}
      {accent && dateLine && (
        <p className="text-[12px] tracking-[0.1em] text-[#7A5C10] font-medium">
          {dateLine}
        </p>
      )}
      <div className={`h-[1px] mx-auto ${accent ? "w-16 bg-[#C5A059]/40" : "w-8 bg-[#3F2A24]/15"}`} />
      {!accent && (
        <div className="max-w-xs mx-auto space-y-4">
          <p className="text-xs text-[#5C4A3D]/80 leading-relaxed whitespace-pre-line" style={{ textWrap: 'balance' }}>
            {desc}
          </p>
          {dateLine && (
            <p className="text-[10px] tracking-[0.08em] text-[#5C4A3D]/50">
              {dateLine}
            </p>
          )}
        </div>
      )}
      <p className={`font-bold text-[#3F2A24] ${accent ? "text-3xl sm:text-4xl" : "text-2xl"}`}>
        {price}
      </p>
      <p className={`text-[#5C4A3D] ${accent ? "text-xs" : "text-[10px] text-[#5C4A3D]/70"}`}>
        {payment}
      </p>
      <div className={accent ? "" : "flex-1"} />
      <div className={accent ? "pt-2" : ""}>
        <button
          onClick={() => onBook(formula.url)}
          data-testid={`wetravel-btn-${id}`}
          className="inline-block rounded-full cursor-pointer text-center transition-all duration-300 min-h-[48px]"
          style={{
            backgroundColor: baseBg,
            color: '#ffffff',
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 500,
            fontSize: '12px',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            padding: '14px 34px',
            boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = hover.bg;
            e.target.style.color = hover.text;
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = baseBg;
            e.target.style.color = '#ffffff';
          }}
        >
          {btnLabel}
        </button>
      </div>
    </div>
  );
};

export default FormulasSection;
